import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { IconFolder } from "@tabler/icons-react";

import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { describeChange, MetricPanel, type Metric } from "@/components/dashboard/metric-cell";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import { MediaSheet, MediaSheetSkeleton, RatioSection } from "@/components/content-programs/media-tile";
import { describeWaiting, submissionActivity } from "@/components/content-programs/submission-activity";
import { SubmissionPortraitTile } from "@/components/brand/content-programs/SubmissionPortraitTile";
import { SubmissionReviewModal } from "@/components/brand/content-programs/SubmissionReviewModal";
import { useRatioGroups } from "@/hooks/useRatioGroups";
import {
  getBrandContentProgram,
  listProgramSubmissions,
  reviewProgramSubmission,
  type ContentSubmissionRow,
} from "@/services/contentProgram.service";
import { listMetaCampaignAds } from "@/services/metaAdsCampaignProgram.service";
import { formatNumber } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import { ContentProgramLaunchTab } from "./ContentProgramLaunchTab";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

type PageTab = "submissions" | "launch";
type StatusTab = "all" | "PENDING" | "CHANGE_REQUESTED" | "APPROVED" | "REJECTED";

/** Waiting first, then the rest, newest on top within each. */
function sortRows(rows: ContentSubmissionRow[]) {
  const rank = (s: string) => (s === "PENDING" ? 0 : s === "CHANGE_REQUESTED" ? 1 : 2);
  return [...rows].sort((a, b) => {
    const r = rank(a.status) - rank(b.status);
    if (r !== 0) return r;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

export default function BrandContentProgramDetail() {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [pageTab, setPageTab] = useState<PageTab>("submissions");
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState<ContentSubmissionRow | null>(null);
  const [launchContentId, setLaunchContentId] = useState<string | null>(null);

  const programQuery = useQuery({
    queryKey: ["brand-content-program", programId],
    queryFn: () => getBrandContentProgram(programId!),
    enabled: Boolean(programId),
  });

  const submissionsQuery = useQuery({
    queryKey: ["brand-content-submissions", programId],
    queryFn: () => listProgramSubmissions(programId!),
    enabled: Boolean(programId),
  });

  const adsQuery = useQuery({
    queryKey: ["brand-content-program-ads", programId],
    queryFn: () => listMetaCampaignAds(programId!),
    enabled: Boolean(programId),
  });

  const reviewMut = useMutation({
    mutationFn: (args: { contentId: string; action: "APPROVE" | "REJECT" | "REQUEST_CHANGE"; message?: string }) =>
      reviewProgramSubmission(programId!, args.contentId, { action: args.action, message: args.message }),
    onSuccess: (row, args) => {
      toast.success(
        args.action === "APPROVE" ? "Submission approved" : args.action === "REJECT" ? "Submission rejected" : "Request sent"
      );
      void submissionsQuery.refetch();
      // A file that answers a TikTok ad request has its ad drafted already:
      // approving it lands on Launch with that creative chosen.
      if (args.action === "APPROVE" && selected?.tiktokAdRequest) {
        setLaunchContentId(row.id);
        setPageTab("launch");
      }
      setSelected(null);
    },
    onError: (e) => toast.error(errMessage(e)),
  });

  const rows = useMemo(() => submissionsQuery.data ?? [], [submissionsQuery.data]);

  const counts = useMemo(() => {
    const c = { all: rows.length, PENDING: 0, CHANGE_REQUESTED: 0, APPROVED: 0, REJECTED: 0 };
    for (const r of rows) {
      if (r.status === "PENDING") c.PENDING += 1;
      else if (r.status === "CHANGE_REQUESTED") c.CHANGE_REQUESTED += 1;
      else if (r.status === "APPROVED" || r.status === "IN_USE") c.APPROVED += 1;
      else if (r.status === "REJECTED") c.REJECTED += 1;
    }
    return c;
  }, [rows]);

  const visible = useMemo(
    () =>
      sortRows(
        statusTab === "all"
          ? rows
          : rows.filter((r) => (statusTab === "APPROVED" ? r.status === "APPROVED" || r.status === "IN_USE" : r.status === statusTab))
      ),
    [rows, statusTab]
  );

  const { orderedGroups, loading: ratiosLoading, getRatioLabel, getMetaRatioLabel } = useRatioGroups(visible);

  const metrics = useMemo<Metric[]>(() => {
    const activity = submissionActivity(rows, days);
    const reviewed = counts.APPROVED + counts.REJECTED;
    const oldestWaiting = rows
      .filter((r) => r.status === "PENDING")
      .reduce<string | null>((acc, r) => (acc === null || r.createdAt < acc ? r.createdAt : acc), null);
    const ads = adsQuery.data ?? [];
    const launched = ads.filter((a) => a.metaAdId).length;
    const failed = ads.filter((a) => a.launchError).length;
    return [
      {
        id: "cp-submissions",
        label: "Submissions",
        value: formatNumber(activity.current),
        change: describeChange(activity.current, activity.previous, days, formatNumber),
        series: activity.series,
        unit: "submission",
      },
      {
        id: "cp-waiting",
        label: "Awaiting review",
        value: formatNumber(counts.PENDING),
        change: describeWaiting(oldestWaiting),
      },
      {
        id: "cp-approved",
        label: "Approved",
        value: formatNumber(counts.APPROVED),
        change: reviewed === 0 ? "No reviews yet" : `${Math.round((counts.APPROVED / reviewed) * 100)}% of reviewed`,
      },
      {
        id: "cp-launched",
        label: "Launched as ads",
        value: formatNumber(launched),
        change:
          failed > 0
            ? `${formatNumber(failed)} failed to launch`
            : counts.APPROVED > launched
              ? `${formatNumber(counts.APPROVED - launched)} approved, not launched`
              : counts.APPROVED === 0
                ? "Approve content to launch"
                : "Every approved creative is live",
      },
    ];
  }, [rows, days, counts, adsQuery.data]);

  const p = programQuery.data;

  if (!programQuery.isLoading && !p) {
    return (
      <div className="space-y-4">
        <PageHeader title="Content program" closeTo="/brand/content-programs" />
        <EmptyState
          title="Program not found"
          body="It may have been removed"
          action={
            <Button variant="outline" onClick={() => navigate("/brand/content-programs")}>
              Back to programs
            </Button>
          }
        />
      </div>
    );
  }

  const facts = p
    ? [
        `${p.commissionRate}% commission`,
        p.isOpenToAllEnrolledAffiliates ? "Open to all enrolled affiliates" : `${p.allowedAffiliates?.length ?? 0} affiliates`,
        p.entitledProductIds?.length ? `${p.entitledProductIds.length} products` : "All products",
      ].join(" · ")
    : "";

  const statusTabs = [
    { value: "all" as const, label: "All", count: counts.all },
    { value: "PENDING" as const, label: "Awaiting review", count: counts.PENDING },
    { value: "CHANGE_REQUESTED" as const, label: "Changes requested", count: counts.CHANGE_REQUESTED },
    { value: "APPROVED" as const, label: "Approved", count: counts.APPROVED },
    { value: "REJECTED" as const, label: "Rejected", count: counts.REJECTED },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title={p?.title ?? "Content program"}
        closeTo="/brand/content-programs"
        customActions={
          p ? [{ id: "edit-program", label: "Edit program", onClick: () => navigate(`/brand/content-programs/${p.id}/edit`) }] : []
        }
      />

      <UnderlineTabs
        tabs={[
          { value: "submissions" as const, label: "Submissions", count: submissionsQuery.data ? counts.all : null },
          { value: "launch" as const, label: "Launch" },
        ]}
        value={pageTab}
        onValueChange={setPageTab}
        accent="brand"
      />

      {pageTab === "submissions" && (
        <>
          <MetricPanel
            id="cp-overview"
            title="Overview"
            metrics={metrics}
            columns="lg:grid-cols-4"
            loading={programQuery.isLoading || submissionsQuery.isLoading}
            className={cn("transition-opacity", submissionsQuery.isFetching && !submissionsQuery.isLoading && "opacity-60")}
            action={<PeriodToggle value={days} onChange={setDays} disabled={submissionsQuery.isLoading} />}
          >
            <div className="flex items-center gap-4 px-5 pb-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
                <IconFolder size={22} stroke={1.5} aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                {p ? (
                  <>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="truncate text-[15px] font-semibold text-foreground">{p.title}</span>
                      <StatusText status={p.status} />
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">{facts}</div>
                  </>
                ) : (
                  <div className="space-y-2 animate-pulse">
                    <div className="h-4 w-48 rounded bg-muted" />
                    <div className="h-3 w-64 rounded bg-muted" />
                  </div>
                )}
              </div>
            </div>
          </MetricPanel>

          <DashboardCard
            title="Submissions"
            fixed={<UnderlineTabs className="mx-5" tabs={statusTabs} value={statusTab} onValueChange={setStatusTab} accent="brand" />}
            bodyClassName="px-5 pb-5 pt-4"
          >
            {submissionsQuery.isLoading || (ratiosLoading && visible.length > 0) ? (
              <MediaSheetSkeleton count={Math.min(Math.max(visible.length, 3), 8)} />
            ) : visible.length === 0 ? (
              <EmptyState
                bordered={false}
                title={statusTab === "all" ? "No submissions yet" : "Nothing here"}
                body={
                  statusTab === "all"
                    ? "Affiliates in this program submit from their portal"
                    : statusTab === "PENDING"
                      ? "Everything has been reviewed"
                      : undefined
                }
                className="py-8"
              />
            ) : (
              <div className="space-y-5">
                {orderedGroups.map((group) => (
                  <RatioSection key={group.key} ratio={group.key} count={group.items.length}>
                    <MediaSheet ratio={group.key}>
                      {group.items.map((row) => (
                        <SubmissionPortraitTile key={row.id} row={row} ratio={getRatioLabel(row.id)} onOpen={setSelected} />
                      ))}
                    </MediaSheet>
                  </RatioSection>
                ))}
              </div>
            )}
          </DashboardCard>
        </>
      )}

      {pageTab === "launch" && p && (
        <ContentProgramLaunchTab
          programId={p.id}
          initialContentId={launchContentId}
          onLaunched={() => {
            setLaunchContentId(null);
            void adsQuery.refetch();
            void submissionsQuery.refetch();
          }}
        />
      )}

      <SubmissionReviewModal
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        submission={selected}
        programTitle={p?.title ?? ""}
        ratioLabel={selected ? getMetaRatioLabel(selected.id) : null}
        isBusy={reviewMut.isPending}
        onApprove={(contentId) => reviewMut.mutate({ contentId, action: "APPROVE" })}
        onReject={(contentId, message) => reviewMut.mutate({ contentId, action: "REJECT", ...(message ? { message } : {}) })}
        onRequestChange={(contentId, message) => reviewMut.mutate({ contentId, action: "REQUEST_CHANGE", message })}
      />
    </div>
  );
}
